import { Link } from 'react-router-dom';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { farmCases } from '@/mocks/smart-farm';

const statusStyle: Record<string, string> = {
  已验证: 'bg-secondary-100 text-secondary-900',
  推进中: 'bg-accent-100 text-accent-900',
};

export default function Cases() {
  const featured = farmCases.find((c) => c.featured);
  const others = farmCases.filter((c) => !c.featured);

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          eyebrow="相关案例"
          title="价值已在真实场景落地"
          subtitle="从城市数字商业到乡村农产品上行，智慧农场的能力在同一条数据链上持续验证与复制。"
        />

        {/* 重点案例 */}
        {featured && (
          <Reveal className="mt-12">
            <div className="rounded-lg border border-accent-200 bg-background-50 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusStyle[featured.status]}`}
                    >
                      {featured.status}
                    </span>
                    <span className="text-xs text-foreground-500">{featured.region}</span>
                  </div>
                  <h3 className="mt-4 font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                    {featured.name}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 font-medium">{featured.scene}</p>

                  <div className="mt-5 space-y-3">
                    <div className="flex gap-3 text-sm leading-relaxed">
                      <span className="shrink-0 font-semibold text-foreground-800">问题</span>
                      <span className="text-foreground-600">{featured.problem}</span>
                    </div>
                    <div className="flex gap-3 text-sm leading-relaxed">
                      <span className="shrink-0 font-semibold text-foreground-800">方案</span>
                      <span className="text-foreground-600">{featured.solution}</span>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {featured.points.map((p) => (
                      <span
                        key={p}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-primary-100 text-primary-900"
                      >
                        {p}
                      </span>
                    ))}
                  </div>

                  <a
                    href={featured.href}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-accent-700 hover:text-accent-800 cursor-pointer whitespace-nowrap"
                  >
                    了解方案落地方式
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>

                <div className="relative min-h-[240px] lg:min-h-full">
                  <img
                    src="https://readdy.ai/api/search-image?query=Digital%20agricultural%20supply%20chain%20connecting%20fresh%20farm%20produce%20from%20rural%20market%20stalls%20to%20urban%20consumers%20through%20a%20glowing%20connected%20network%20of%20data%20nodes%20and%20traceability%20scanning%2C%20stylized%20editorial%20illustration%2C%20warm%20natural%20green%20and%20gold%20tones%2C%20clean%20modern%20composition%2C%20high%20detail&width=800&height=520&seq=smart-farm-case-featured-01&orientation=landscape"
                    alt="中仙数智商贸 智慧农场解决方案"
                    title="中仙数智商贸 智慧农场解决方案"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-background-50/70 to-transparent lg:from-transparent"></div>
                </div>
              </div>
            </div>
          </Reveal>
        )}

        {/* 关联案例 */}
        <Reveal className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {others.map((item) => (
              <div
                key={item.id}
                className="flex flex-col p-6 rounded-lg bg-background-100 border border-background-200 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusStyle[item.status]}`}
                  >
                    {item.status}
                  </span>
                  <span className="text-xs text-foreground-500">{item.region}</span>
                </div>

                <h3 className="mt-4 font-heading font-bold text-xl text-foreground-950">{item.name}</h3>
                <p className="mt-1 text-sm text-foreground-600 font-medium">{item.scene}</p>

                <div className="mt-4 space-y-2 text-sm leading-relaxed flex-1">
                  <p className="text-foreground-600">
                    <span className="font-semibold text-foreground-800">问题：</span>
                    {item.problem}
                  </p>
                  <p className="text-foreground-600">
                    <span className="font-semibold text-foreground-800">方案：</span>
                    {item.solution}
                  </p>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {item.points.map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900"
                    >
                      {p}
                    </span>
                  ))}
                </div>

                <Link
                  to={item.href}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap"
                >
                  查看案例
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal className="mt-8">
          <p className="text-xs text-foreground-500 leading-relaxed max-w-3xl">
            注：案例名称、合作细节与量化成效需经业务与法务授权确认后展示，当前以方案能力示意呈现，不代表已实现的经营结果。
          </p>
        </Reveal>
      </div>
    </section>
  );
}